import { z } from "zod";
import { LocalRunRecordSchema, WorkspaceContextSchema, type LocalRunRecord } from "./localRun";
import { buildSignalDecisions, SignalDecisionSchema } from "./decisions";

const uuid = z.string().uuid();
const runId = z.string().regex(/^rev_[0-9a-f-]{36}$/);
const actionKey = z.enum(["release-path", "revision", "collect-proof"]);
const timestamp = z.string().datetime();
const note = z.string().max(2000);
export const PROJECT_ACTION_MAX_BYTES = 256 * 1024;
const EvidenceSchema = z.object({
  id: uuid, label: z.string().min(1).max(300), reference: z.string().min(1).max(2048),
  source: z.literal("artist_supplied"), verification: z.literal("unverified"), suppliedAt: timestamp
}).strict();
const ContextSchema = z.object({
  id: uuid, runId, definitionKey: SignalDecisionSchema.shape.definitionKey,
  title: z.string().min(1).max(4000), definitionOfDone: z.string().min(1).max(4000),
  dependencyIds: z.array(z.string().min(1).max(100)).max(3)
}).strict();
const EventSchema = z.object({
  id: uuid, contextId: uuid, recordedAt: timestamp,
  kind: z.enum(["status_reported", "evidence_supplied", "review_requested"]),
  status: z.enum(["open", "in_progress", "done"]).optional(), note,
  evidence: EvidenceSchema.optional(), authority: z.literal("local_self_report")
}).strict().superRefine((event, ctx) => {
  if ((event.kind === "status_reported") !== (event.status !== undefined) ||
      (event.kind === "evidence_supplied") !== (event.evidence !== undefined))
    ctx.addIssue({code:z.ZodIssueCode.custom,message:"Event payload must match its declared kind"});
});
const ActionSchema = z.object({
  stableId: z.string().min(1).max(100), key: actionKey,
  owner: z.object({artistRecordId: WorkspaceContextSchema.shape.artistRecordId, authority:z.literal("unverified_local_reference")}).strict(),
  currentContextId: uuid, contexts: z.array(ContextSchema).min(1).max(20), events: z.array(EventSchema).max(200)
}).strict();

/** Local/imported records cannot assert authenticated review, consent or release approval. */
export const ProjectActionLedgerSchema = z.object({
  recordVersion:z.literal(1), projectId:WorkspaceContextSchema.shape.projectId,
  artistRecordId:WorkspaceContextSchema.shape.artistRecordId, currentRunId:runId,
  ownership:z.literal("unverified_local_reference"), actions:z.array(ActionSchema).length(3),
  legacyClaims:z.array(z.object({title:z.string().max(4000),status:z.enum(["open","planned","in_progress","done"]),disposition:z.literal("unassigned_unverified")}).strict()).max(100)
}).strict().superRefine((ledger, ctx) => {
  if(new TextEncoder().encode(JSON.stringify(ledger)).length>PROJECT_ACTION_MAX_BYTES)
    ctx.addIssue({code:z.ZodIssueCode.custom,message:"Project action history exceeds its recovery limit"});
  const ids = new Set(ledger.actions.map(a=>a.stableId));
  if (ids.size !== 3 || new Set(ledger.actions.map(a=>a.key)).size !== 3)
    ctx.addIssue({code:z.ZodIssueCode.custom,message:"Action identity must be unique"});
  const allIds = new Set<string>();
  for (const action of ledger.actions) {
    if (action.stableId !== stableActionId(ledger.projectId, action.key) || action.owner.artistRecordId !== ledger.artistRecordId)
      ctx.addIssue({code:z.ZodIssueCode.custom,message:"Action ownership differs from project context"});
    const contexts = new Map(action.contexts.map(c=>[c.id,c]));
    const current = contexts.get(action.currentContextId);
    if (contexts.size !== action.contexts.length || !current || current.runId !== ledger.currentRunId)
      ctx.addIssue({code:z.ZodIssueCode.custom,message:"Current action context must belong to the current run"});
    for (const context of action.contexts) {
      if (allIds.has(context.id)) ctx.addIssue({code:z.ZodIssueCode.custom,message:"Duplicate context identifier"});
      allIds.add(context.id);
      if (new Set(context.dependencyIds).size !== context.dependencyIds.length || context.dependencyIds.some(id=>!ids.has(id)||id===action.stableId))
        ctx.addIssue({code:z.ZodIssueCode.custom,message:"Missing, repeated or self dependency"});
    }
    for (const event of action.events) {
      if (!contexts.has(event.contextId) || allIds.has(event.id)) ctx.addIssue({code:z.ZodIssueCode.custom,message:"Event belongs to an unknown context or repeats an identifier"});
      allIds.add(event.id);
      if (event.evidence) {
        if (allIds.has(event.evidence.id)) ctx.addIssue({code:z.ZodIssueCode.custom,message:"Duplicate evidence identifier"});
        allIds.add(event.evidence.id);
      }
    }
  }
  const visiting = new Set<string>(), visited = new Set<string>();
  const visit = (id:string):boolean => {
    if (visiting.has(id)) return false;
    if (visited.has(id)) return true;
    visiting.add(id);
    const action=ledger.actions.find(a=>a.stableId===id);
    const context=action?.contexts.find(c=>c.id===action.currentContextId);
    if (context?.dependencyIds.some(dep=>!visit(dep))) return false;
    visiting.delete(id); visited.add(id); return true;
  };
  if (ledger.actions.some(a=>!visit(a.stableId))) ctx.addIssue({code:z.ZodIssueCode.custom,message:"Current dependency graph contains a cycle"});
});
export type ProjectActionLedger = z.infer<typeof ProjectActionLedgerSchema>;
export type ProjectActionEvent = z.infer<typeof EventSchema>;
export const stableActionId = (projectId:string, key:z.infer<typeof actionKey>) => `${projectId}:${key}`;

export function createProjectActions(record:LocalRunRecord, previous?:unknown, legacy?:unknown):ProjectActionLedger {
  const valid=LocalRunRecordSchema.parse(record), refs=valid.run.references;
  const projectId=WorkspaceContextSchema.parse({recordVersion:1,artistRecordId:refs.artistRecordId,projectId:refs.projectId}).projectId;
  const parsed=ProjectActionLedgerSchema.safeParse(previous);
  if (previous !== undefined && previous !== null && !parsed.success) throw new Error("Invalid previous project actions; preserve for recovery");
  if (parsed.success && (parsed.data.projectId!==refs.projectId || parsed.data.artistRecordId!==refs.artistRecordId))
    throw new Error("Project actions belong to a different local workspace");
  const summary=buildSignalDecisions(valid.run.review,valid);
  const actions=summary.decisions.map(decision=>{
    const stableId=stableActionId(projectId,decision.id);
    const dependencyIds=decision.id==="release-path" ? [stableActionId(projectId,"collect-proof"),stableActionId(projectId,"revision")] :
      decision.id==="revision" && summary.missingInputs.length ? [stableActionId(projectId,"collect-proof")] : [];
    const old=parsed.success?parsed.data.actions.find(a=>a.stableId===stableId):undefined;
    const current=old?.contexts.find(c=>c.id===old.currentContextId);
    // Display wording may change. Run, semantic definition and dependencies cannot inherit claims silently.
    const same=current?.runId===refs.runId && current.definitionKey===decision.definitionKey &&
      current.definitionOfDone===decision.definitionOfDone && JSON.stringify(current.dependencyIds)===JSON.stringify(dependencyIds);
    const context={id:same?current.id:crypto.randomUUID(),runId:refs.runId,definitionKey:decision.definitionKey,
      title:decision.answer,definitionOfDone:decision.definitionOfDone,dependencyIds};
    return {stableId,key:decision.id,owner:{artistRecordId:refs.artistRecordId,authority:"unverified_local_reference" as const},
      currentContextId:context.id,contexts:same?old!.contexts.map(c=>c.id===context.id?context:c):[...(old?.contexts??[]),context],events:old?.events??[]};
  });
  const claims=parsed.success?parsed.data.legacyClaims:legacyClaims(legacy);
  return ProjectActionLedgerSchema.parse({recordVersion:1,projectId:refs.projectId,artistRecordId:refs.artistRecordId,
    currentRunId:refs.runId,ownership:"unverified_local_reference",actions,legacyClaims:claims});
}

function legacyClaims(input:unknown):ProjectActionLedger["legacyClaims"] {
  if (input===undefined || input===null) return [];
  const map=z.record(z.string().max(4000),z.enum(["open","planned","in_progress","done"])).safeParse(input);
  if (!map.success || Object.keys(map.data).length>100) throw new Error("Malformed or oversized legacy actions; preserve for recovery");
  // Text alone cannot establish which artist/project/run originally performed this work.
  return Object.entries(map.data).map(([title,status])=>({title,status,disposition:"unassigned_unverified"}));
}

export function recordProjectAction(ledger:ProjectActionLedger, stableId:string, event:Omit<ProjectActionEvent,"id"|"contextId"|"recordedAt"|"authority">):ProjectActionLedger {
  const copy=ProjectActionLedgerSchema.parse(ledger), action=copy.actions.find(a=>a.stableId===stableId);
  if (!action) throw new Error("Unknown project action");
  action.events.push(EventSchema.parse({...event,id:crypto.randomUUID(),contextId:action.currentContextId,recordedAt:new Date().toISOString(),authority:"local_self_report"}));
  return ProjectActionLedgerSchema.parse(copy);
}

export function resolveProjectAction(ledger:ProjectActionLedger, stableId:string) {
  const valid=ProjectActionLedgerSchema.parse(ledger), action=valid.actions.find(a=>a.stableId===stableId);
  if (!action) throw new Error("Unknown project action");
  const context=action.contexts.find(c=>c.id===action.currentContextId)!;
  const events=action.events.filter(e=>e.contextId===context.id);
  const status=events.filter(e=>e.kind==="status_reported").at(-1)?.status??"open";
  const evidence=events.flatMap(e=>e.evidence?[e.evidence]:[]);
  return {stableId,projectId:valid.projectId,runId:context.runId,title:context.title,definitionOfDone:context.definitionOfDone,
    reportedStatus:status,evidence,reviewRequested:events.some(e=>e.kind==="review_requested"),
    unresolvedDependencies:context.dependencyIds,reviewedCompletion:false as const,
    state:context.dependencyIds.length?"blocked_by_unreviewed_dependencies" as const:status==="done"?"self_reported_done" as const:status,
    reason:context.dependencyIds.length?"Dependency proof has not been reviewed. Local status and supplied evidence cannot clear it.":
      "Local progress is self-reported. Supplied evidence and review requests do not establish reviewed completion or release approval.",
    historicalEventCount:action.events.length-events.length};
}

export function exportProjectActions(ledger:ProjectActionLedger):string {
  const text=JSON.stringify(ProjectActionLedgerSchema.parse(ledger));
  if (new TextEncoder().encode(text).length>PROJECT_ACTION_MAX_BYTES) throw new Error("Project action export exceeds recovery limit");
  return text;
}

/** Merge preserves newer local history. Conflicting reused IDs are never silently preferred. */
export function mergeProjectActionHistories(existing:ProjectActionLedger,incoming:ProjectActionLedger):ProjectActionLedger {
  const local=ProjectActionLedgerSchema.parse(existing), imported=ProjectActionLedgerSchema.parse(incoming);
  if(local.projectId!==imported.projectId || local.artistRecordId!==imported.artistRecordId) throw new Error("Project action histories belong to different workspaces");
  const union=<T extends {id:string}>(first:T[],second:T[]):T[]=>{
    const map=new Map(first.map(value=>[value.id,value]));
    for(const value of second){const prior=map.get(value.id);if(prior&&JSON.stringify(prior)!==JSON.stringify(value))throw new Error("Conflicting action history identifier");if(!prior)map.set(value.id,value);}
    return [...map.values()];
  };
  for(const action of local.actions){
    const other=imported.actions.find(a=>a.stableId===action.stableId)!;
    action.contexts=union(action.contexts,other.contexts);
    const localEventIds=new Set(action.events.map(event=>event.id));
    action.events=union(action.events,other.events).sort((a,b)=>Date.parse(a.recordedAt)-Date.parse(b.recordedAt)||Number(localEventIds.has(a.id))-Number(localEventIds.has(b.id)));
  }
  const claims=new Map(local.legacyClaims.map(claim=>[JSON.stringify(claim),claim]));
  for(const claim of imported.legacyClaims)claims.set(JSON.stringify(claim),claim);
  local.legacyClaims=[...claims.values()];
  return ProjectActionLedgerSchema.parse(local);
}
export function importProjectActions(text:string, workspace:unknown):ProjectActionLedger {
  if (text.length>PROJECT_ACTION_MAX_BYTES || new TextEncoder().encode(text).length>PROJECT_ACTION_MAX_BYTES) throw new Error("Project action import exceeds limit");
  const owner=WorkspaceContextSchema.parse(workspace), ledger=ProjectActionLedgerSchema.parse(JSON.parse(text));
  if (ledger.projectId!==owner.projectId || ledger.artistRecordId!==owner.artistRecordId) throw new Error("Project action import belongs to a different local workspace");
  return ledger;
}
